import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { getSubdomain } from '../../utils/utils';
import { Mixpanel } from '../../utils/mixpanel';
import { AICA_MAGIC_UPLOAD } from '../../utils/constants/mixpanelEvents/investorEvents';
import { MAGIC_UPLOAD_TYPE } from '../../store/investor/constants';
import { applyBorrowerMagicUploadParams } from './Utils/MagicUploadUtils';
import { MU_ENTRY_POINT, TAB_STATE_ALL } from './Utils/MagicUploadConstants';
import { PendingUpload, enqueuePendingUpload } from './pendingUploads/pendingUploadStore';

export type OpenMagicUploadOptions = {
    /** Files captured on a tab dropzone (click or drop), enqueued before the drawer opens. */
    files?: File[];
    /** Which UploadJourney tab triggered the open, for the Mixpanel event. */
    uploadTab?: string;
    /** Untagged-fallback docType applied only if Pandora leaves the file untagged (RECUR-28067 v4). */
    fallbackDocType?: string;
    /**
     * Set to false to suppress the AICA_MAGIC_UPLOAD Mixpanel event for this call (RECUR-28067
     * review fix). Used when a click-then-pick interaction already tracked the open on the click
     * call, so the follow-up file-selection call for the SAME interaction doesn't double-count it.
     * Defaults to true — a bare open (e.g. a drop, with no prior click) always tracks.
     */
    track?: boolean;
};

/**
 * Opens the borrower Magic Upload drawer from an UploadJourney tab, using the exact
 * same params Topbar's "Upload Data" click sets (applyBorrowerMagicUploadParams),
 * so the drawer that opens is provably the same one (RECUR-28067 D4).
 *
 * Also forces the drawer onto the "All" sub-tab (tabState=all) so a queued file can
 * always drain into the mounted MagicUploadCenter, even if the drawer was last left
 * on New/Tagged (SF5).
 */
const useOpenMagicUpload = () => {
    const [, setSearchParams] = useSearchParams();
    const user = useAppSelector((state) => state.user);
    const assesseeId = useAppSelector((state) => state.user.userData.assesseeOrgId);

    const openMagicUpload = (options: OpenMagicUploadOptions = {}) => {
        const { files, uploadTab, fallbackDocType, track = true } = options;

        if (files && files.length > 0) {
            const entry: PendingUpload = { files, uploadTab, fallbackDocType };
            enqueuePendingUpload(entry);
        }

        setSearchParams((currentParams) => {
            const nextParams = applyBorrowerMagicUploadParams(new URLSearchParams(currentParams));
            nextParams.set('tabState', TAB_STATE_ALL);
            return nextParams;
        });

        if (track) {
            Mixpanel.track(AICA_MAGIC_UPLOAD, {
                userRole: user?.userData?.accountType,
                pnoPlatform: getSubdomain(),
                pnoId: user?.userData?.investorOrgId,
                userId: user?.userData?.userId,
                currentUrl: window.location.href,
                accessedPlatform: user?.userData?.accountType,
                individualGlobal: MAGIC_UPLOAD_TYPE.INDIVIDUAL,
                organizationId: assesseeId,
                entryPoint: MU_ENTRY_POINT.UPLOAD_JOURNEY,
                uploadTab,
            });
        }
    };

    return openMagicUpload;
};

export default useOpenMagicUpload;
